import { useEffect, useState } from 'react'

import { NetworkStatus } from '@apollo/client'
import { Alert, Loader } from '@mantine/core'
import { useInView } from 'react-intersection-observer'
import { Applicant } from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import MiniCallLog from 'src/components/ActivityLogs/CallLog/MiniCallLog'
import EmailLog from 'src/components/ActivityLogs/EmailLog/EmailLog'
import NoteLog from 'src/components/ActivityLogs/NoteLog/NoteLog'
import SMSLog from 'src/components/ActivityLogs/SMSLog/SMSLog'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_ACTIVITY_LOGS } from 'src/graphql/activityLogs.graphql'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'

import ActivityLogInput from '../../tabs/ActivityLogTab/components/ActivityLogInput/ActivityLogInput'
import RefreshButton from '../../tabs/ActivityLogTab/components/RefreshButton'
type CommunicationType = 'NOTE' | 'SMS' | 'CALL' | 'EMAIL'

const SidebarActivityLogs = ({
  applicant,
  communication = 'NOTE',
  onRegisterCall,
}: {
  applicant: Applicant
  communication?: CommunicationType
  onRegisterCall?: (activityLog) => void
}) => {
  const LIMIT = 10
  const page = 1
  const defaultFilters = {
    activityLogTypes: [communication],
  }
  const [filters, setFilters] = useState(defaultFilters)
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  })

  const onError = () => {
    toast('Unable to get activity logs, please try again.', 'error')
  }

  useEffect(() => {
    setFilters({ activityLogTypes: [communication] })
  }, [communication])

  const variables = {
    applicantId: applicant.applicantId,
    filters,
    pageInfo: { offset: (page - 1) * LIMIT, limit: LIMIT },
  }

  const {
    data: activityLogsResponse,
    networkStatus,
    fetchMore,
    refetch,
    loading,
  } = useQuery(GET_ACTIVITY_LOGS, {
    notifyOnNetworkStatusChange: true,
    variables,
    onError,
  })

  const refetchQueries = [
    {
      query: GET_ACTIVITY_LOGS,
      variables,
    },
  ]

  useEffect(() => {
    if (inView) {
      fetchMore({
        variables: {
          applicantId: applicant.applicantId,
          filters,
          pageInfo: {
            offset: activityLogsResponse?.activityLogs.items.length ?? 0,
            limit: LIMIT,
          },
        },
      })
    }
  }, [inView])

  const isSMSOptedOut =
    communication === 'SMS' && applicant.isCommunicationOptIn === false
  const isCallOptedOut =
    communication === 'CALL' && applicant.isCommunicationOptIn === false

  const items = activityLogsResponse?.activityLogs.items ?? []
  // The server orders by createdAt (webhook sync time), but email bubbles
  // display email.date (real send/receive time) — a delayed Nylas sync would
  // shuffle the thread relative to its own timestamps. Re-sort the loaded
  // email thread by the same date the bubbles show so the order is always
  // consistent top-to-bottom.
  const displayItems =
    communication === 'EMAIL'
      ? [...items].sort(
          (a, b) =>
            new Date(b.email?.date ?? b.createdAt).getTime() -
            new Date(a.email?.date ?? a.createdAt).getTime()
        )
      : items

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-hidden pb-0.5 pt-2">
      {/* todo bug: fix refetch issue across components */}
      <div
        className={`flex flex-1 flex-col-reverse overflow-auto ${
          communication === 'NOTE' ? 'gap-4' : 'gap-2'
        }`}
      >
        {/* Inbound texts and emails arrive via webhooks — give those threads a
            manual refresh so new messages show without a remount. */}
        {(communication === 'SMS' || communication === 'EMAIL') && (
          <RefreshButton loading={loading} refetch={refetch} />
        )}
        {displayItems.map((activityLog) => {
          if (activityLog.type === 'SMS') {
            return (
              <SMSLog
                key={activityLog.activityLogId}
                applicant={applicant}
                activityLog={activityLog}
              />
            )
          } else if (activityLog.type === 'NOTE') {
            return (
              <NoteLog
                key={activityLog.activityLogId}
                activityLog={activityLog}
                refetchQueries={refetchQueries}
              />
            )
          } else if (activityLog.type === 'CALL') {
            return (
              <MiniCallLog
                key={activityLog.activityLogId}
                applicant={applicant}
                activityLog={activityLog}
              />
            )
          } else if (activityLog.type === 'EMAIL') {
            return (
              <EmailLog
                key={activityLog.activityLogId}
                applicant={applicant}
                activityLog={activityLog}
              />
            )
          }
        })}
        {networkStatus === NetworkStatus.fetchMore && <Loader size="sm" />}
        {activityLogsResponse?.activityLogs.items.length <
          activityLogsResponse?.activityLogs.totalCount && (
          <div ref={ref} className="opacity-0">
            Inview placeholder
          </div>
        )}
      </div>

      {isCallOptedOut && (
        <Alert
          icon={
            <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-warning-500" />
          }
          color="yellow"
          variant="light"
          classNames={{
            root: 'py-2',
            body: 'gap-0',
            message: 'text-xs',
          }}
        >
          This applicant has opted out. AI-assisted calls will not be triggered.
          They can opt back in by texting <strong>START</strong> or{' '}
          <strong>SUBSCRIBE</strong>.
        </Alert>
      )}

      {isSMSOptedOut ? (
        <Alert
          icon={
            <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-error-500" />
          }
          color="red"
          variant="light"
          classNames={{
            root: 'py-2',
            body: 'gap-0',
            message: 'text-xs',
          }}
        >
          This applicant has opted out of text messages. They can opt back in by
          texting <strong>START</strong> or <strong>SUBSCRIBE</strong>.
        </Alert>
      ) : (
        // Emails have no inline composer in this thread — the Email tab keeps
        // its own "Send email" entry point (compose modal) instead.
        communication !== 'EMAIL' && (
          <div>
            <ActivityLogInput
              applicantId={applicant.applicantId}
              communication={communication}
              refetchQueries={refetchQueries}
              onRegisterCall={onRegisterCall}
            />
          </div>
        )
      )}
    </div>
  )
}

export default SidebarActivityLogs
